/*
 * Authors: mariushirjanu
 * Created: 28/03/2024
 */
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Column, GridOptions, RowClassParams, ValueFormatterParams } from 'ag-grid-community';
import { IPaymentMatch, PaymentMatcherService } from './payment-matcher.service';
import _ from 'underscore';
import { CSS_ACTIONS_COLUMN, IFormGridColDef } from '@app/shared/form-grid/form-grid.component';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { AG_CELL_VALIDATOR, AgCellValidatorComponent } from '@app/shared/ag-grid-components/ag-cell-validator/ag-cell-validator.component';
import { TranslationService } from '@app/shared/translation/translation.service';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { IPaymentMatchableInvoice, PaymentMatcherDataService, PaymentMatchStatus } from './payment-matcher-data.service';
import { ICompanyCountry } from '@type_definitions/datatypes';
import { IAgButtonRendererParams } from '@app/shared/ag-grid-components/ag-button/ag-button-renderer.component';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '@app/shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { urlUtil } from '@app/shared/util';
import { URL_INVOICE_EDIT } from '@app/shared/browser/url-collections';
import { GRID_PAYMENT_LIST_INVOICES } from '@app/shared/app-grids';
import { GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';

interface IPaymentListInvoicesModel {
  issuedTo?:ICompanyCountry[];
  manualSearch?:string;
}

@Component({
  selector: 'payment-list-invoices',
  template: `
    <h4 class="form-title">Open Outgoing Invoices</h4>

    <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true" class="form-payment-list-invoices"></formly>

    <span class="clearfix"></span>

    <form-grid
      [gridId]="gridId"
      [loading]="loading"
      [gridOptions]="gridOptions"
      [rowData]="rowData"
      [height]="formGridHeight"
    >
    </form-grid>
  `,
  styleUrls: ['./payment-matcher-dialog.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentListInvoicesComponent implements OnInit, OnChanges {
  @Input() public transactionId:string;
  @Input() public processingStatus:PaymentMatchStatus;
  @Input() public invoiceToAdd:IPaymentMatch;
  @Output() public onRemoveFromInvoices:EventEmitter<IPaymentMatch> = new EventEmitter<IPaymentMatch>();
  @Output() public onIssuedToChange:EventEmitter<ICompanyCountry[]> = new EventEmitter<ICompanyCountry[]>();

  public loading:boolean;
  public gridOptions:GridOptions;
  public rowData:IPaymentMatch[];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IPaymentListInvoicesModel;
  public formGridHeight:number;
  public gridId:string = GRID_PAYMENT_LIST_INVOICES;
  private searchButton:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private paymentMatcherService:PaymentMatcherService,
    private paymentMatcherDataService:PaymentMatcherDataService,
    private validateService:GaValidateService,
    private gaAgGridService:GaAgGridService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    /***/
  }

  public ngOnInit():void {
    this.formGridHeight = this.paymentMatcherService.calcFormGridHeight;
    this.fields = this.createFields();
    this.installGrid();
    this.loadData();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    // receive new invoices removed from payment matches list to add to invoice list
    if (changes.invoiceToAdd?.currentValue) {
      this.invoiceToAdd = changes.invoiceToAdd.currentValue;
      this.addInvoice();
    }
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aMultiSelect('issuedTo', 'Issued to')
        .withWrapperClass('col-lg-5')
        .withLabelClass('col-sm-3 col-md-5 col-lg-3 col-xxl-2')
        .withValueClass('col-sm-9 col-md-7 col-lg-9 col-xxl-10')
        .withFormat(this.formattingService.formatCompanyCountry)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.paymentMatcherDataService.getTransactionIssuedTo(this.transactionId, filter)
            },
            minSearchLength: 2
          }
        })
        .onChange((issuedTo:ICompanyCountry[]) => {
          this.onIssuedToChange.emit(issuedTo);
        })
        .build(),

      this.formlyService.anInput('manualSearch', 'Invoice manual search')
        .withWrapperClass('col-lg-5')
        .withLabelClass('col-sm-3 col-md-5 col-lg-5 col-xxl-3')
        .withValueClass('col-sm-9 col-md-7 col-lg-7 col-xxl-9')
        .onEnter(this.onSearch)
        .build(),

      this.searchButton = this.formlyService.aLink('GA.BUTTONS.SEARCH')
        .withoutWrapper()
        .withWrapperClass('col-md-2 margin-bottom-md')
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn btn-primary no-margin'
          }
        })
        .onClick(this.onSearch)
        .build()
    ];
  }

  private get colDefs():IFormGridColDef[] {
    const statusNewOrProposed:boolean = this.paymentMatcherService.isStatusNewOrProposed(this.processingStatus);
    const colDefs:IFormGridColDef[] = [
      {
        pinned: 'left',
        minWidth: 74,
        width: 74,
        cellClass: CSS_ACTIONS_COLUMN,
        tooltipValueGetter: () => statusNewOrProposed ? 'Add match' : '',
        cellRenderer: CellRendererComp.BUTTON_RENDERER,
        cellRendererParams: {
          label: this.translationService.instant('GA.BUTTONS.ADD'),
          icon: 'fa-arrow-up',
          onClick: (params:IAgButtonRendererParams) => {
            this.addMatch(params.data?.id);
          },
          disabled: !statusNewOrProposed,
          className: 'btn-sm btn-success'
        }
      },
      {
        field: 'id',
        headerName: 'Invoice ID',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{
            label: params.value,
            href: urlUtil.replaceParams(URL_INVOICE_EDIT, { invoiceId: params.value })
          }]
        }
      },
      {
        field: 'invoiceNumber',
        headerName: 'Invoice number'
      },
      {
        field: 'orderId',
        headerName: 'Request ID'
      },
      {
        field: 'poNumber',
        headerName: 'PO number'
      },

      {
        field: 'amount',
        headerName: 'Amount',
        type: 'rightAligned',
        valueFormatter: this.formatNumber
      },
      {
        field: 'remainingAmount',
        headerName: 'Remaining',
        type: 'rightAligned',
        valueFormatter: this.formatNumber
      },
      {
        field: 'currency',
        headerName: 'Currency'
      },
      {
        field: 'invoiceDate',
        headerName: 'Invoice date',
        valueFormatter: (params:ValueFormatterParams) => this.formattingService.formatDate(params.value)
      },
      {
        field: 'invoicedTo',
        headerName: 'Invoiced to'
      },
      {
        field: 'country',
        headerName: 'Country'
      },
      {
        field: 'bankAccount',
        headerName: 'Account number'
      },
      {
        field: 'invoiceType',
        headerName: 'Invoice type'
      },
      {
        field: 'batchBillId',
        headerName: 'Batch bill ID',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{
            label: params.value,
            href: urlUtil.replaceParams(URL_INVOICE_EDIT, { invoiceId: params.value })
          }]
        }
      },
      {
        field: 'batchBillInvoiceNumber',
        headerName: 'Batch bill number'
      }
    ];

    return _.map(colDefs, (colDef:IFormGridColDef) => {
      colDef.cellRenderer = colDef.cellRenderer || AG_CELL_VALIDATOR; // apply 'agCellValidator' to columns in order to display validation errors
      // colDef.tooltipField = ''; // hide default ag-grid tooltip (instead is being used the popover defined in AG_CELL_VALIDATOR)

      colDef.minWidth = colDef.minWidth || 100;
      colDef.resizable = true;
      colDef.editable = colDef.editable || false;
      colDef.sortable = true;

      return colDef;
    });
  }

  private installGrid():void {
    this.gridOptions = {
      columnDefs: this.colDefs,
      suppressScrollOnNewData: true, // do not scroll to the beginning of the grid
      components: {
        [AG_CELL_VALIDATOR]: AgCellValidatorComponent
      },
      onRowDataUpdated: () => {
        //auto size all column except row-actions
        const columns:Column[] = _.reject(this.gaAgGridService.getColumns(this.gridId), (column:Column) => column.getColDef().cellClass === CSS_ACTIONS_COLUMN);
        return this.gaAgGridService.autoSizeColumns(this.gridId, columns);
      },
      rowClassRules: {
        // highlight new added rows
        'highlight-row': (params:RowClassParams) => {
          return params.data?.highlight;
        }
      }
    };
  }

  private loadData():void {
    this.loading = true;
    this.paymentMatcherDataService.getTransactionIssuedTo(this.transactionId)
      .then((companyCountries:ICompanyCountry[]) => {
        this.model = { issuedTo: companyCountries };
        return this.transactionInvoicesPromise;
      })
      .then((invoices:IPaymentMatch[]) => {
        this.rowData = invoices;
      })
      .catch(() => {
        this.rowData = []; // when promise fails -> stop spinner
      })
      .finally(() => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  private onSearch = ():void => {
    this.validate()
      .then(() => {
        this.loading = true;
        this.searchButton.templateOptions.busy = true;
        this.transactionInvoicesPromise
          .then((invoices:IPaymentMatch[]) => {
            this.rowData = invoices;
          })
          .catch(() => {
            this.rowData = []; // when promise fails -> stop spinner
          })
          .finally(() => {
            this.loading = false;
            this.searchButton.templateOptions.busy = false;
            this.changeDetectorRef.detectChanges();
          });
      });
  };

  private addMatch(transactionId:string):void {
    const addMatch:IPaymentMatch = _.find(this.rowData, (rowData:IPaymentMatch) => rowData.id === transactionId);
    this.onRemoveFromInvoices.emit(addMatch);
    this.rowData = _.reject(this.rowData, (rowData:IPaymentMatch) => rowData.id === transactionId);
  }

  private addInvoice():void {
    if(this.invoiceToAdd?.id) {
      this.invoiceToAdd.highlight = true;
      this.rowData = this.rowData.concat([ this.invoiceToAdd ]);
    }
  }

  private get transactionInvoicesPromise():Promise<IPaymentMatchableInvoice[]> {
    const ccIds:string = !_.isEmpty(this.model.issuedTo) ? this.formattingService.formatCSV(this.model.issuedTo) : undefined;
    return this.paymentMatcherDataService.getTransactionInvoices(this.transactionId, ccIds, this.model.manualSearch || undefined);
  }

  public validate = ():Promise<void|string[]> => {
    return this.validateService.validateFields(this.fields, this.model);
  };

  private formatNumber = (params:ValueFormatterParams):string => {
    return this.formattingService.formatNumber(params.value);
  };
}